import Dictionary = _.Dictionary;

type IEnumerationDefinitionDatatype =
  | IEnumerationDefinitionComplexDatatype
  | IEnumerationDefinitionSimpleDatatype
  | IEnumerationDefinitionComplexDatatype[]
  | string[];
type EnumeratedSetDefinitionType = undefined | IEnumeratedSetValueDatatype | string | number;
type MeldOperations = 'union' | 'difference' | 'intersection';

export interface IEnumerationDefinitionTypeComplex {
  ordinal?: number;
  key?: string;
  value?: string;
  [propName: string]: any;
}

export interface IEnumerationDefinitionComplexDatatype {
  [propName: string]: IEnumerationDefinitionTypeComplex;
}

export interface IEnumerationDefinitionSimpleDatatype {
  [propName: string]: string;
}

export interface IEnumeratedSetValueDatatype {
  readonly ordinal: number;
  readonly key: string;
  readonly id: string;
  readonly value: any;
}

export interface IEnumeratedSetContract {
  has(unknown: EnumeratedSetDefinitionType): boolean;
  size(): number;
  length(): number;
  get(unknown: any): EnumeratedSetValue | undefined;
  key(unknown: string): EnumeratedSetValue;
  ordinal(unknown: number | string): EnumeratedSetValue;
  value(unknown: any): EnumeratedSetValue;
  human(unknown: any): EnumeratedSetValue;
  iterator(): Dictionary<{}>;
  isEqual(e1: EnumeratedSetDefinitionType, e2: EnumeratedSetDefinitionType): boolean;
  random(): EnumeratedSetValue;
  union(enumeration: EnumeratedSet): EnumeratedSet;
  difference(enumeration: EnumeratedSet): EnumeratedSet;
  intersection(enumeration: EnumeratedSet): EnumeratedSet;
  meld(enumeration: EnumeratedSet, operation: MeldOperations): EnumeratedSet;
  variableOf(): Function;
  applyIsMethodsTo(applyTo: Object): Object;
  overwrite(enumerationDefinition: IEnumerationDefinitionDatatype): EnumeratedSet;
  replace(enumerationDefinition: IEnumerationDefinitionDatatype): EnumeratedSet;
  clear(): EnumeratedSet;
  addAsObject(enumerationDefinition: IEnumerationDefinitionDatatype): EnumeratedSet;
  add(enumerationDefinition: IEnumerationDefinitionDatatype): EnumeratedSet;
  addAsValue(enumerationDefinition: IEnumerationDefinitionDatatype): EnumeratedSet;
  globallyAccessibleWith(name: string): boolean;
  global(name: string): boolean;
  toString(): string;
}

export class EnumeratedSetValue implements IEnumeratedSetValueDatatype {
  constructor(private _key: string, private _ordinal: number, private _value?: any) {
    this._key = (this._key + '').toLowerCase();
  }

  assignProperties(o: Object): EnumeratedSetValue {
    _.each(o, (v: any, k: any): void => {
      this[k] = v;
    });

    EnumeratedSetValue.assignReadOnlyToProperties(_.keys(this), this);
    return this;
  }

  get key(): string {
    return this._key;
  }

  get ordinal(): number {
    return this._ordinal;
  }

  get value(): any {
    return this._value;
  }

  /** @deprecated */
  get id(): string {
    return this.key;
  }

  get isEnum(): boolean {
    return true;
  }

  /** @deprecated use Enumeration.isEqual(e, e) */
  isEqual(enumeration: undefined | string | EnumeratedSetValue): boolean {
    if (!enumeration) {
      return false;
    } else if (_.isString(enumeration)) {
      return enumeration.toLowerCase() === this.key;
    } else if (enumeration.isEnum) {
      return enumeration.key === this.key;
    }

    return false;
  }

  static assignReadOnlyToProperties = (properties: string[], o: any): any => {
    _.each(properties, (property: string) => {
      Object.defineProperty(o, property, {
        configurable: false,
        writable: false
      });
    });

    return o;
  };
}

export class EnumeratedSet implements IEnumeratedSetContract {
  private __keys__: Dictionary<EnumeratedSetValue>;
  private __values__: Dictionary<EnumeratedSetValue>;
  private __ordinals__: Dictionary<EnumeratedSetValue>;
  private _name: string = '';

  constructor() {
    this.clear();
  }

  get name(): string {
    return this._name;
  }

  get isEnumType(): boolean {
    return true;
  }

  get isEnum(): boolean {
    return this.isEnumType;
  }

  has(unknown: EnumeratedSetDefinitionType): boolean {
    return (
      _.isUndefined(this.get(unknown)) || (_.isObject(unknown) && _.isUndefined(_.get(this.key(unknown + ''), 'key')))
    );
  }

  size(): number {
    return _.size(this.__ordinals__) as number;
  }

  length(): number {
    return this.size() as number;
  }

  get(unknown: any): EnumeratedSetValue | undefined {
    let known: EnumeratedSetValue;

    if (_.isUndefined(unknown)) {
      return undefined;
    }

    if (unknown && unknown.key) {
      return unknown as EnumeratedSetValue;
    }

    known = this.key(unknown as string);
    if (known) {
      return known;
    }

    known = this.ordinal(unknown as number);
    if (known) {
      return known;
    }

    return undefined;
  }

  key(unknown: string): EnumeratedSetValue {
    return this.__keys__[(unknown + '').toUpperCase()] as EnumeratedSetValue;
  }

  ordinal(unknown: number | string): EnumeratedSetValue {
    return this.__ordinals__[parseInt(unknown + '', 10)];
  }

  value(unknown: any): EnumeratedSetValue {
    const valueKey: string | any = unknown && unknown.key ? unknown.key : unknown;

    return this.__values__[valueKey];
  }

  human(unknown: any): EnumeratedSetValue {
    return this.value(unknown);
  }

  iterator(): Dictionary<{}> {
    return this.__keys__;
  }

  isEqual(e1: EnumeratedSetDefinitionType, e2: EnumeratedSetDefinitionType): boolean {
    return this.getStringValue(e1) === this.getStringValue(e2);
  }

  private getStringValue(enumeration: EnumeratedSetDefinitionType): number {
    const possibleEnumeration: EnumeratedSetValue | undefined = this.get(enumeration);

    return !!possibleEnumeration && possibleEnumeration.isEnum
      ? (possibleEnumeration as EnumeratedSetValue).ordinal
      : NaN;
  }

  random(): EnumeratedSetValue {
    return this.ordinal(_.random(0, this.size()));
  }

  /** union with another enumeration */
  union(enumeration: EnumeratedSet): EnumeratedSet {
    return this.meld(enumeration, 'union');
  }

  /** difference with another enumeration, removing this enumeration */
  difference(enumeration: EnumeratedSet): EnumeratedSet {
    return this.meld(enumeration, 'difference');
  }

  /** intersection with another enumeration, adding/removing this enumeration */
  intersection(enumeration: EnumeratedSet): EnumeratedSet {
    return this.meld(enumeration, 'intersection');
  }

  /** apply a lodash function to an enumeration, combining this enumeration with another */
  meld(enumeration: EnumeratedSet, operation: MeldOperations): EnumeratedSet {
    const meldOperation: (keys1: any[], keys2: any[]) => any[] = _[operation];
    const keys: any[] = _.uniq(_.compact(meldOperation(_.keys(this.__keys__), _.keys(enumeration.__keys__))));
    const newEnum: IEnumerationDefinitionDatatype = {};
    const enumKeys: Object = enumeration.__keys__;
    const thisKeys: Object = this.__keys__;

    // QUESTION what if they have the same keys?

    _.each(keys, (value: any): void => {
      if (enumKeys[value]) {
        newEnum[value] = enumKeys[value];
      } else if (thisKeys[value]) {
        newEnum[value] = thisKeys[value];
      }
    });

    return this.replace(newEnum);
  }

  variableOf(): Function {
    const enumerationType: EnumeratedSet = this;
    let v: EnumeratedSetValue | undefined;

    return function (val: EnumeratedSetDefinitionType): EnumeratedSetValue | undefined {
      let potentialNewValue: EnumeratedSetValue | undefined;

      if (val) {
        potentialNewValue = enumerationType.get(val);

        if (!!potentialNewValue) {
          v = potentialNewValue;
        }
      }

      return v;
    };
  }

  applyIsMethodsTo(applyTo: Object): Object {
    const enumName: string = _.first((this.name || 'Enum').split('Enum')) as string;
    const enumDef: EnumeratedSet = this;

    _.each(this.__keys__, function (keyDef: any, key: string) {
      applyTo['is' + enumName + EnumeratedSet.convertKeyToMethodName(key)] = (value: any): boolean => {
        return enumDef.isEqual(key, value);
      };
    });

    return applyTo;
  }

  /** @deprecated */
  overwrite(enumerationDefinition: IEnumerationDefinitionDatatype): EnumeratedSet {
    return this.replace(enumerationDefinition);
  }

  replace(enumerationDefinition: IEnumerationDefinitionDatatype): EnumeratedSet {
    this.clear();
    return this.add(enumerationDefinition);
  }

  clear(): EnumeratedSet {
    this.__ordinals__ = {};
    this.__values__ = {};
    this.__keys__ = {};
    return this;
  }

  /** read enumeration description and add it to this enumeration */
  addAsObject(enumerationDefinition: IEnumerationDefinitionDatatype): EnumeratedSet {
    const _enumerationDefinition: Object = EnumeratedSet.convertEnumerationDefinitionAsArray(enumerationDefinition);
    let valueCount: number = _.size(this.__keys__);

    _.each(_enumerationDefinition, (enumDef: IEnumerationDefinitionTypeComplex, key: string): void => {
      let newEnum: EnumeratedSetValue;
      const ordinal: number = enumDef && enumDef.ordinal ? enumDef.ordinal : valueCount;

      key = (key + '').toUpperCase();

      delete enumDef.ordinal;
      delete enumDef.key;

      newEnum = new EnumeratedSetValue(key, ordinal).assignProperties(enumDef);
      valueCount = newEnum.ordinal + 1;

      this[key] = newEnum;
      this.__keys__[key] = newEnum;
      this.__ordinals__[newEnum.ordinal] = newEnum;
      this.__values__[newEnum + ''] = newEnum;

      this.addShorthandIsMethod(key);
    });

    return this;
  }

  static convertEnumerationDefinitionAsArray(enumerationDefinition: any): Object {
    if (!_.isArray(enumerationDefinition)) {
      return enumerationDefinition;
    }

    return _.has(enumerationDefinition, '[0].key')
      ? _.zipObject(
          _.map(enumerationDefinition, (v: any): string => v.key),
          enumerationDefinition
        )
      : _.zipObject(enumerationDefinition as any[], enumerationDefinition);
  }

  private addShorthandIsMethod(key: string): void {
    this['is' + EnumeratedSet.convertKeyToMethodName(key)] = (
      possibleEnumeration: EnumeratedSetDefinitionType
    ): boolean => {
      return this.isEqual(key, possibleEnumeration);
    };
  }

  private static SPLIT_BY_UNDERSCORE: RegExp = /_/g;

  private static convertKeyToMethodName(key: string): string {
    return _.map(key.split(EnumeratedSet.SPLIT_BY_UNDERSCORE), (word) => {
      return _.capitalize(word.toLowerCase());
    }).join('');
  }

  add(enumerationDefinition: IEnumerationDefinitionDatatype): EnumeratedSet {
    return this.addAsValue(enumerationDefinition);
  }

  /** read enumeration value description and add it to this enumeration */
  addAsValue(enumerationDefinition: IEnumerationDefinitionDatatype): EnumeratedSet {
    const _enumerationDefinition: Object = EnumeratedSet.convertEnumerationDefinitionAsArray(enumerationDefinition);
    let valueCount: number = _.size(this.__keys__);

    _.each(_enumerationDefinition, (obj: IEnumerationDefinitionTypeComplex, key: string): void => {
      let enumDef: EnumeratedSetValue;

      let ordinal: number = obj && obj.ordinal ? obj.ordinal : valueCount;

      let value: any = obj && obj['value'] ? obj['value'] : obj;

      key = (key + '').toUpperCase();
      enumDef = new EnumeratedSetValue(key, ordinal, value);
      valueCount = ordinal + 1;

      this[key] = enumDef;
      this.__keys__[key] = enumDef;
      this.__ordinals__[ordinal] = enumDef;
      this.__values__[value.toString()] = enumDef;

      this.addShorthandIsMethod(key);
    });

    return this;
  }

  globallyAccessibleWith(name: string): boolean {
    window['enums'] = EnumerationBuilder.__registered__;

    this._name = name;
    window[name] = this;
    window['enums'][name] = this;

    return this.name && window['enums'][this.name];
  }

  /** @deprecated */
  global(name: string): boolean {
    return this.globallyAccessibleWith(name);
  }

  /**  */
  toString(): string {
    return JSON.stringify(_.keys(this.__keys__));
  }
}

export class EnumerationBuilder {
  private static _registered: Object = {};

  static get __registered__(): Object {
    return EnumerationBuilder._registered;
  }

  static useDefinitionAsValue(
    name: IEnumerationDefinitionDatatype | string,
    optionalConfiguration?: IEnumerationDefinitionDatatype
  ): EnumeratedSet {
    return EnumerationBuilder.parseOptions(
      arguments,
      (enumObj: EnumeratedSet, configuration: IEnumerationDefinitionDatatype): EnumeratedSet => {
        return enumObj.addAsValue(configuration);
      }
    );
  }

  static useDefinitionAsEnum(
    name: IEnumerationDefinitionDatatype | string,
    optionalConfiguration?: IEnumerationDefinitionDatatype
  ): EnumeratedSet {
    return EnumerationBuilder.parseOptions(
      arguments,
      (enumObj: EnumeratedSet, configuration: IEnumerationDefinitionDatatype): EnumeratedSet => {
        return enumObj.addAsObject(configuration);
      }
    );
  }

  private static parseOptions(args: any, constructorFn: Function) {
    let name: string = typeof args[0] === 'string' ? args[0] : '';

    let configuration: IEnumerationDefinitionDatatype = typeof args[0] === 'string' ? args[1] : args[0];

    const enumObj: EnumeratedSet = new EnumeratedSet();
    const hasUniqueName: boolean = !_.isUndefined(configuration);

    if (hasUniqueName) {
      enumObj.globallyAccessibleWith(name);
    } else {
      configuration = _.isString(name) ? {} : name;
    }

    return constructorFn(enumObj, configuration as IEnumerationDefinitionDatatype);
  }
}

export interface IEnumeration {
  (
    name: IEnumerationDefinitionDatatype | string,
    optionalConfiguration?: IEnumerationDefinitionDatatype
  ): EnumeratedSet;
  useDefinitionAsValue: (
    name: IEnumerationDefinitionDatatype | string,
    optionalConfiguration?: IEnumerationDefinitionDatatype
  ) => EnumeratedSet;
  useDefinitionAsEnum: (
    name: IEnumerationDefinitionDatatype | string,
    optionalConfiguration?: IEnumerationDefinitionDatatype
  ) => EnumeratedSet;
}

const Enumeration: any = function Enumeration(
  name: IEnumerationDefinitionDatatype | string,
  optionalConfiguration?: IEnumerationDefinitionDatatype
): EnumeratedSet {
  return EnumerationBuilder.useDefinitionAsValue(name, optionalConfiguration);
};

Enumeration.useDefinitionAsValue = (
  name: IEnumerationDefinitionDatatype | string,
  optionalConfiguration?: IEnumerationDefinitionDatatype
): EnumeratedSet => {
  return EnumerationBuilder.useDefinitionAsValue(name, optionalConfiguration);
};

Enumeration.useDefinitionAsEnum = (
  name: IEnumerationDefinitionDatatype | string,
  optionalConfiguration?: IEnumerationDefinitionDatatype
): EnumeratedSet => {
  return EnumerationBuilder.useDefinitionAsEnum(name, optionalConfiguration);
};

Enumeration.__registered__ = EnumerationBuilder.__registered__;

export default Enumeration;



// WEBPACK FOOTER //
// ./app/vendor/utility-js-enumeration/src/main.ts